import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [currentMusic, setCurrentMusic] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.05); // Start at 5%
  const [isPaused, setIsPaused] = useState(false);
  const audioRef = useRef(null);
  const otherAudioPausedMusic = useRef(false);
  const pendingMusic = useRef(null); // Store music that couldn't play due to autoplay restrictions

  // Two constants on purpose: API carries the /api prefix every route needs, BACKEND is the
  // bare origin for stored file paths, which already begin with /api themselves.
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND}/api`;

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.loop = true;
    audioRef.current.volume = volume;

    // Handle audio end (shouldn't happen with loop, but just in case)
    audioRef.current.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    // Add global click handler to play pending music
    const handleUserInteraction = () => {
      if (pendingMusic.current && audioRef.current && !isPlaying) {
        console.log('User interaction detected - playing pending music');
        playMusic(pendingMusic.current);
      }
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Update volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Fetch and play music for current page/module
  const fetchAndPlayMusic = async (page, moduleId = null) => {
    try {
      const params = {};
      if (page) params.page = page;
      if (moduleId) params.module_id = moduleId;

      // API is REACT_APP_BACKEND_URL + /api. The s58 route audit stripped the /api from this
      // call on the assumption that API already carried it; it did not, so the request hit the
      // React app, came back as index.html with a 200, `success` was undefined and stopMusic()
      // ran — which is why the player disappeared from the whole platform.
      const response = await axios.get(`${API}/background-music/current`, { params });
      
      if (response.data.success && response.data.music) {
        const music = response.data.music;
        
        // Only change music if it's different
        if (!currentMusic || currentMusic.id !== music.id) {
          setCurrentMusic(music);
          playMusic(music);
        }
      } else {
        // No music assigned, stop current music
        stopMusic();
      }
    } catch (error) {
      console.error('Failed to fetch background music:', error);
    }
  };

  const playMusic = (music) => {
    if (!audioRef.current || !music) return;

    try {
      audioRef.current.src = `${BACKEND}${music.filepath}`;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsPaused(false);
          pendingMusic.current = null; // Clear pending music on success
        })
        .catch(error => {
          console.log('Autoplay blocked - music will start on next user interaction');
          // Store this music to play on next user interaction
          pendingMusic.current = music;
        });
    } catch (error) {
      console.error('Error playing music:', error);
    }
  };

  const pauseMusic = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsPaused(true);
    }
  };

  const resumeMusic = () => {
    if (audioRef.current && isPaused && currentMusic) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsPaused(false);
        })
        .catch(error => {
          console.error('Failed to resume music:', error);
        });
    }
  };

  const stopMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentMusic(null);
    }
  };

  // Prime/unlock the audio element within a user gesture so later programmatic
  // playback is allowed (required on mobile/iOS). Used by the Welcome Back button.
  const unlockAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.muted = true;
      a.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          a.pause();
          a.currentTime = 0;
          a.muted = false;
          a.src = '';
        }).catch(() => {
          a.muted = false;
          a.src = '';
        });
      }
    } catch (e) {
      /* ignore — unlock is best-effort */
    }
  };

  // Auto-pause when other audio plays
  const pauseForOtherAudio = () => {
    if (isPlaying) {
      pauseMusic();
      otherAudioPausedMusic.current = true;
    }
  };

  // Auto-resume when other audio stops
  const resumeAfterOtherAudio = () => {
    if (otherAudioPausedMusic.current && currentMusic) {
      resumeMusic();
      otherAudioPausedMusic.current = false;
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseMusic();
    } else if (isPaused) {
      resumeMusic();
    } else if (currentMusic) {
      playMusic(currentMusic);
    }
  };

  const value = {
    currentMusic,
    isPlaying,
    isPaused,
    volume,
    setVolume,
    fetchAndPlayMusic,
    pauseMusic,
    resumeMusic,
    stopMusic,
    unlockAudio,
    togglePlayPause,
    pauseForOtherAudio,
    resumeAfterOtherAudio
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
